import React from 'react';
import { Loader, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Play, FileText, Copy, MessageSquare, ArrowRight, Rocket } from 'lucide-react';
import FlowVisualization from './FlowVisualization';

const PromptsView = ({
  isDarkMode, generatedPrompts, isGeneratingPrompts, selectedPromptNodeIndex,
  isPromptFlowExpanded, promptTestResults, isRunningPromptTest, requestJsonOutput,
  workflowResult, currentFlowIndex, agentDefinition,
  // Callbacks
  setSelectedPromptNodeIndex, setIsPromptFlowExpanded, setRequestJsonOutput,
  setPromptTestResults, setExecutionNodes, setExecutionStatus, setCurrentExecutingNodeIndex,
  setCurrentView, handleTestPrompt, handleCopyPrompt,
}) => {

  // Loading state: early return if !generatedPrompts && isGeneratingPrompts
  if (!generatedPrompts && isGeneratingPrompts) {
    return (
      /* Loading state while prompts are being generated */
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <Loader className={`w-10 h-10 animate-spin ${isDarkMode ? 'text-cyan-400' : 'text-[#0077A8]'}`} />
        <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-200' : 'text-[#0077A8]'}`}>Generating Prompts...</h3>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Creating optimized prompts for each node in your workflow
        </p>
      </div>
    );
  }

  // Full view: if generatedPrompts
  if (generatedPrompts) {
    return (
      <div className="space-y-4">
        {/* Collapsible Flow Visualization - click to expand/collapse */}
        {/* Node stepper bar - always visible, toggles expand/collapse */}
        <div
          className="group rounded-lg px-4 py-3 cursor-pointer transition-all duration-300 hover:brightness-110 hover:shadow-lg"
          style={{
            background: isDarkMode
              ? 'rgba(30, 41, 59, 0.9)'
              : 'linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)'
          }}
          onClick={() => setIsPromptFlowExpanded(!isPromptFlowExpanded)}
        >
          <div className="flex items-center justify-between">
            {/* Minimal stepper */}
            <div className="flex items-center space-x-1 flex-1 overflow-x-auto">
              {generatedPrompts.generated_prompts.map((prompt, index) => {
                const isSelected = selectedPromptNodeIndex === index;
                return (
                  <React.Fragment key={prompt.node_id}>
                    {index > 0 && (
                      <div className={`w-4 h-px flex-shrink-0 transition-colors duration-300 ${
                        isDarkMode ? 'bg-gray-600 group-hover:bg-white/30' : 'bg-white/40 group-hover:bg-white/60'
                      }`} />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPromptNodeIndex(index);
                      }}
                      className={`flex items-center space-x-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all flex-shrink-0 text-black ${
                        isSelected
                          ? 'bg-[#0077A8] group-hover:bg-white'
                          : 'bg-white/50 hover:bg-white/70 group-hover:bg-white/80'
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                        isSelected
                          ? 'bg-black/20'
                          : 'bg-black/10 group-hover:bg-black/15'
                      }`}>
                        {index + 1}
                      </span>
                      <span className="truncate max-w-[80px]">{prompt.display_name}</span>
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
            {/* Expand/collapse hint with animation */}
            <div className="ml-3 flex items-center space-x-2 flex-shrink-0 transition-all duration-300">
              <span className={`text-xs font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 ${
                isDarkMode ? 'text-white' : 'text-white'
              }`}>
                {isPromptFlowExpanded ? 'click to collapse' : 'click to expand'}
              </span>
              {isPromptFlowExpanded ? (
                <ChevronUp className={`w-4 h-4 transition-all duration-300 group-hover:-translate-y-0.5 ${
                  isDarkMode ? 'text-gray-400 group-hover:text-white' : 'text-white/80 group-hover:text-white'
                }`} />
              ) : (
                <ChevronDown className={`w-4 h-4 transition-all duration-300 group-hover:translate-y-0.5 ${
                  isDarkMode ? 'text-gray-400 group-hover:text-white' : 'text-white/80 group-hover:text-white'
                }`} />
              )}
            </div>
          </div>
        </div>

        {/* Expanded: Full FlowVisualization */}
        {isPromptFlowExpanded && (
          <div className={`rounded-lg transition-all duration-300 ${isDarkMode ? 'bg-gray-800/30 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
            <div className="px-4 py-4" style={{ overflow: 'visible', minHeight: '200px' }}>
              <FlowVisualization
                nodes={(workflowResult?.flows?.[currentFlowIndex]?.workflow_steps || workflowResult?.primary_workflow || []).map((step, idx) => ({
                  nodeId: step.node?.node_id || `node-${idx}`,
                  alias: step.node?.display_name || `Node ${idx + 1}`,
                  description: step.node?.business_function || ''
                }))}
                workflowSteps={workflowResult?.flows?.[currentFlowIndex]?.workflow_steps || workflowResult?.primary_workflow || []}
                isDarkMode={isDarkMode}
                agentDefinition={agentDefinition}
                variableMappings={null}
                currentNodeIndex={selectedPromptNodeIndex}
                onNodeClick={(idx) => {
                  const workflowSteps = workflowResult?.flows?.[currentFlowIndex]?.workflow_steps || workflowResult?.primary_workflow || [];
                  let promptIdx = 0;
                  for (let i = 0; i < Math.min(idx, workflowSteps.length); i++) {
                    const nodeType = (workflowSteps[i]?.node?.type || workflowSteps[i]?.node?.nodeType || '').toLowerCase();
                    if (['llm', 'promptdel', 'ai'].includes(nodeType)) {
                      promptIdx++;
                    }
                  }
                  const clickedNodeType = (workflowSteps[idx]?.node?.type || workflowSteps[idx]?.node?.nodeType || '').toLowerCase();
                  if (['llm', 'promptdel', 'ai'].includes(clickedNodeType)) {
                    setSelectedPromptNodeIndex(promptIdx);
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Node Header and Test Section - Outside the card */}
        {generatedPrompts.generated_prompts[selectedPromptNodeIndex] && (
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${isDarkMode ? 'bg-blue-900/50 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-700 border border-blue-300'}`}>
                  Step {generatedPrompts.generated_prompts[selectedPromptNodeIndex].position}
                </span>
                <span className={`text-xs font-medium px-2 py-1 rounded border ${isDarkMode ? 'bg-purple-900/50 text-purple-300 border-purple-700' : 'bg-purple-100 text-purple-700 border border-purple-300'}`}>
                  LLM Node
                </span>
              </div>
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                {generatedPrompts.generated_prompts[selectedPromptNodeIndex].display_name}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Node ID: {generatedPrompts.generated_prompts[selectedPromptNodeIndex].node_id}
              </p>
            </div>
            {/* Test This Node Section */}
            <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-[#0077A8]/20 border border-[#0077A8]/50' : 'bg-[#0077A8]/10 border border-[#0077A8]/30'}`}>
              <div className="flex items-center justify-between gap-6">
                <div>
                  <h4 className={`font-semibold flex items-center ${isDarkMode ? 'text-cyan-300' : 'text-[#0077A8]'}`}>
                    <Play className="w-4 h-4 mr-2" />
                    Test This Node
                  </h4>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-cyan-200/70' : 'text-[#0077A8]/70'}`}>
                    {promptTestResults[selectedPromptNodeIndex]
                      ? 'Modify the input below and run again to test different scenarios'
                      : 'Run this node with AI-generated sample data'}
                  </p>
                  {/* JSON Output Toggle */}
                  <label className={`flex items-center mt-2 cursor-pointer ${isDarkMode ? 'text-cyan-200/70' : 'text-[#0077A8]/70'}`}>
                    <input
                      type="checkbox"
                      checked={requestJsonOutput}
                      onChange={(e) => setRequestJsonOutput(e.target.checked)}
                      className="mr-2 rounded"
                    />
                    <span className="text-xs">Request JSON output</span>
                  </label>
                </div>
                <button
                  onClick={handleTestPrompt}
                  disabled={isRunningPromptTest}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 ${
                    isRunningPromptTest
                      ? 'bg-gray-500 cursor-not-allowed'
                      : isDarkMode ? 'bg-[#0077A8] hover:bg-[#006090] text-white' : 'bg-[#0077A8] hover:bg-[#006090] text-white'
                  }`}
                >
                  {isRunningPromptTest ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Running...</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span>{promptTestResults[selectedPromptNodeIndex] ? 'Run Again' : 'Run Test'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Selected Node Prompt Details */}
        {generatedPrompts.generated_prompts[selectedPromptNodeIndex] && (() => {
          const selectedPrompt = generatedPrompts.generated_prompts[selectedPromptNodeIndex];
          return (
            <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-lg`}>
              {/* System Prompt */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm font-semibold flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FileText className="w-4 h-4 mr-2" />
                    System Prompt
                  </label>
                  <button
                    onClick={() => handleCopyPrompt(selectedPrompt.system_prompt)}
                    className={`p-1.5 rounded-md text-xs flex items-center space-x-1 ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                  >
                    <Copy className="w-3 h-3" />
                    <span>Copy</span>
                  </button>
                </div>
                <div className={`rounded-lg p-4 max-h-48 overflow-y-auto ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                  <pre className={`text-sm whitespace-pre-wrap font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {selectedPrompt.system_prompt}
                  </pre>
                </div>
              </div>

              {/* User Prompt */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm font-semibold flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    User Prompt
                  </label>
                  <button
                    onClick={() => handleCopyPrompt(selectedPrompt.user_prompt)}
                    className={`p-1.5 rounded-md text-xs flex items-center space-x-1 ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                  >
                    <Copy className="w-3 h-3" />
                    <span>Copy</span>
                  </button>
                </div>
                <div className={`rounded-lg p-4 max-h-48 overflow-y-auto ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                  <pre className={`text-sm whitespace-pre-wrap font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {selectedPrompt.user_prompt}
                  </pre>
                </div>
              </div>

              {/* Test Results Section */}
              {promptTestResults[selectedPromptNodeIndex] ? (
                <div className="space-y-4">
                  {/* Test Input - Editable */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={`text-sm font-semibold flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <ArrowRight className="w-4 h-4 mr-2 text-yellow-500" />
                        Test Input
                        <span className={`ml-2 text-xs font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          (edit to test different scenarios)
                        </span>
                      </label>
                    </div>
                    <textarea
                      value={promptTestResults[selectedPromptNodeIndex]?.input || ''}
                      onChange={(e) => setPromptTestResults(prev => ({
                        ...prev,
                        [selectedPromptNodeIndex]: {
                          ...prev[selectedPromptNodeIndex],
                          input: e.target.value
                        }
                      }))}
                      rows={5}
                      className={`w-full rounded-lg p-4 text-sm font-mono resize-y ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-700/50 text-yellow-300 placeholder-yellow-700' : 'bg-yellow-50 border border-yellow-200 text-yellow-800 placeholder-yellow-400'}`}
                      placeholder="Modify this input and click 'Run Again' to test..."
                    />
                  </div>

                  {/* Test Output - Read-only */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={`text-sm font-semibold flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <ArrowRight className="w-4 h-4 mr-2 text-green-500" />
                        Test Output
                      </label>
                      <button
                        onClick={() => handleCopyPrompt(promptTestResults[selectedPromptNodeIndex]?.output || '')}
                        className={`p-1.5 rounded-md text-xs flex items-center space-x-1 ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                      >
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </button>
                    </div>
                    <div className={`rounded-lg p-4 max-h-64 overflow-y-auto text-left ${isDarkMode ? 'bg-green-900/20 border border-green-700/50' : 'bg-green-50 border border-green-200'}`}>
                      <pre className={`text-sm whitespace-pre-wrap font-mono text-left m-0 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>{promptTestResults[selectedPromptNodeIndex]?.output || ''}</pre>
                    </div>
                  </div>
                </div>
              ) : (
                /* Before first run - show expected format info */
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-700/30 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <Play className={`w-4 h-4 ${isDarkMode ? 'text-cyan-400' : 'text-[#0077A8]'}`} />
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Click "Run Test" to see this prompt in action
                    </span>
                  </div>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    The AI will generate realistic sample input and show you the output. You can then modify the input to test different scenarios.
                  </p>
                  {selectedPrompt.expected_output_format && (
                    <div className={`mt-3 pt-3 border-t border-dashed ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Expected output format:
                      </span>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {selectedPrompt.expected_output_format}
                      </p>
                    </div>
                  )}
                </div>
              )}

            </div>
          );
        })()}

        {/* Bottom Navigation */}
        <div className="flex items-center justify-between pt-4">
          <button
            onClick={() => setCurrentView('map-data')}
            className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
              isDarkMode
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Back to Map Data</span>
          </button>
          <button
            onClick={() => {
              // Reset execution state
              setExecutionNodes([]);
              setExecutionStatus(null);
              setCurrentExecutingNodeIndex(-1);
              setIsPromptFlowExpanded(true); // Expand flow by default
              setCurrentView('execute');
            }}
            className="px-6 py-2 bg-[#0077A8] hover:bg-[#006090] text-white rounded-lg font-medium flex items-center space-x-2"
          >
            <Rocket className="w-4 h-4" />
            <span>Run Agent</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Fallback: return null
  return null;
};

export default PromptsView;
